import React from 'react';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { mdiPlus, mdiTrashCan, mdiPencil, mdiFileUpload } from '@mdi/js';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import Botao from '@/components/Botao';
import Grid, { GridItem } from '@/components/layout/Grid';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoNumero from '@/components/Formulario/CampoNumero';
import CampoCheckbox from '@/components/Formulario/CampoCheckbox';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoData from '@/components/Formulario/CampoData';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import AlertBox from '@/components/layout/AlertBox';
import Modal from '@/components/layout/Modal';
import { CampoDataset, Dataset, camposAtivos } from '@tera/shared/src/types/Dataset';
import ModalImportarDataset from './ModalImportarDataset';

type Props = {
    dataset: Dataset;
};

/** Campo de edição adequado ao tipo declarado no schema. */
const EditorValor = ({
    campo,
    valor,
    onChange,
}: {
    campo: CampoDataset;
    valor: any;
    onChange: (v: any) => void;
}) => {
    if (campo.valores?.length) {
        return (
            <CampoSeletor
                label={campo.rotulo}
                options={campo.valores.map((v) => ({ id: v, label: <span>{v}</span> }))}
                value={valor ?? null}
                onChange={onChange}
            />
        );
    }

    switch (campo.tipo) {
        case 'number':
            // CampoNumero opera em string; o backend coage para número ao salvar.
            return (
                <CampoNumero
                    label={campo.rotulo}
                    value={valor != null ? String(valor) : ''}
                    onChange={onChange}
                    decimais={campo.formato === 'moeda' ? 2 : 4}
                />
            );
        case 'boolean':
            return (
                <CampoCheckbox label={campo.rotulo} value={!!valor} onChange={onChange} />
            );
        case 'date':
        case 'datetime':
            return (
                <CampoData
                    label={campo.rotulo}
                    value={valor ?? ''}
                    onChange={onChange}
                    type={campo.tipo === 'datetime' ? 'datetime-local' : 'date'}
                />
            );
        default:
            return (
                <CampoTexto label={campo.rotulo} value={valor ?? ''} onChange={onChange} />
            );
    }
};

const RegistrosDataset = ({ dataset }: Props) => {
    const [emEdicao, setEmEdicao] = React.useState<Record<string, any> | null>(null);
    const [aExcluir, setAExcluir] = React.useState<number | null>(null);
    const [importando, setImportando] = React.useState(false);

    const campos = camposAtivos({ campos: dataset.campos ?? [] });

    const query = useQuery(['dataset-registros', dataset.chave], async () => {
        const res = await axios.get(`/api/dataset/${dataset.chave}/registros`, {
            params: { t: 100 },
        });
        return res.data;
    });

    const salvarMutation = useMutation(
        async (dados: Record<string, any>) =>
            axios.post(`/api/dataset/${dataset.chave}/registros`, [dados]),
        {
            onSuccess: (res: any) => {
                const erros = res.data?.result?.erros ?? [];
                if (erros.length) {
                    toast(erros[0].erro, { type: 'error', position: 'top-center' });
                    return;
                }
                toast('Registro salvo', { type: 'success', position: 'top-center' });
                query.refetch();
                setEmEdicao(null);
            },
            onError: (err: any) =>
                toast(err.response?.data?.msg ?? 'Erro ao salvar registro', {
                    type: 'error',
                    position: 'top-center',
                }),
        },
    );

    const excluirMutation = useMutation(
        async (id: number) => axios.delete(`/api/dataset/${dataset.chave}/registros/${id}`),
        {
            onSuccess: () => {
                toast('Registro excluído', { type: 'success', position: 'top-center' });
                query.refetch();
                setAExcluir(null);
            },
        },
    );

    if (!campos.length) {
        return (
            <AlertBox variant="info">
                <p>Defina os campos da tabela antes de cadastrar registros.</p>
            </AlertBox>
        );
    }

    const cabecalhos: DataTableHeader[] = [
        ...campos.map((campo) => ({
            label: campo.rotulo,
            render: (linha: any) => <span>{formatar(linha.dados?.[campo.chave], campo)}</span>,
        })),
        {
            label: 'Ações',
            width: '120px',
            render: (linha: any) => (
                <HBox gap={8}>
                    <Botao
                        variant="none-primary"
                        title="Editar"
                        icon={mdiPencil}
                        onClick={() => setEmEdicao({ ...linha.dados })}
                    />
                    <Botao
                        variant="none-danger"
                        title="Excluir"
                        icon={mdiTrashCan}
                        onClick={() => setAExcluir(linha.id)}
                    />
                </HBox>
            ),
        },
    ];

    return (
        <VBox gap={16}>
            <HBox gap={8}>
                <div style={{ flex: 1 }}>
                    <p>
                        {query.data?.total ?? 0} registro(s). Envio em massa por CSV ou pela API:{' '}
                        <code>POST /api/dataset/{dataset.chave}/registros</code>
                        {dataset.campos_chave?.length
                            ? ` (atualiza pelo par ${dataset.campos_chave.join(' + ')})`
                            : ''}
                        .
                    </p>
                </div>
                <Botao
                    variant="secondary"
                    icon={mdiFileUpload}
                    title="Importar CSV"
                    onClick={() => setImportando(true)}
                >
                    Importar CSV
                </Botao>
                <Botao
                    variant="primary"
                    icon={mdiPlus}
                    title="Novo registro"
                    onClick={() => setEmEdicao({})}
                >
                    Novo registro
                </Botao>
            </HBox>

            <DataTable
                isLoading={query.isLoading}
                cabecalhos={cabecalhos}
                linhas={query.data?.result ?? []}
            />

            <Modal
                instanceId="dataset-registro-form"
                show={!!emEdicao}
                title="Registro"
                size="lg"
                onClose={() => setEmEdicao(null)}
                onCancel={() => setEmEdicao(null)}
                onConfirm={() => emEdicao && salvarMutation.mutate(emEdicao)}
                confirmProps={{
                    label: 'Salvar',
                    variant: 'primary',
                    disabled: salvarMutation.isLoading,
                }}
                cancelProps={{ label: 'Cancelar' }}
            >
                <Grid>
                    {campos.map((campo) => (
                        <GridItem sm={12} md={6} key={campo.chave}>
                            <EditorValor
                                campo={campo}
                                valor={emEdicao?.[campo.chave]}
                                onChange={(v) =>
                                    setEmEdicao((atual) => ({ ...(atual ?? {}), [campo.chave]: v }))
                                }
                            />
                        </GridItem>
                    ))}
                </Grid>
            </Modal>

            <ModalImportarDataset
                dataset={dataset}
                show={importando}
                onClose={() => setImportando(false)}
                onSuccess={() => query.refetch()}
            />

            <Modal
                instanceId="dataset-registro-delete"
                show={!!aExcluir}
                title="Confirmar exclusão"
                size="sm"
                onClose={() => setAExcluir(null)}
                onCancel={() => setAExcluir(null)}
                onConfirm={() => aExcluir && excluirMutation.mutate(aExcluir)}
                confirmProps={{ label: 'Confirmar', variant: 'danger' }}
                cancelProps={{ label: 'Cancelar' }}
            >
                <p>Tem certeza que deseja excluir este registro?</p>
            </Modal>
        </VBox>
    );
};

function formatar(valor: any, campo: CampoDataset): string {
    if (valor === undefined || valor === null || valor === '') return '—';

    if (campo.tipo === 'boolean') return valor ? 'Sim' : 'Não';

    if (campo.tipo === 'number' && campo.formato === 'moeda')
        return Number(valor).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        });

    if (campo.tipo === 'number' && campo.formato === 'percentual')
        return `${Number(valor).toLocaleString('pt-BR')}%`;

    if (campo.tipo === 'date') return new Date(valor).toLocaleDateString('pt-BR');
    if (campo.tipo === 'datetime') return new Date(valor).toLocaleString('pt-BR');

    return String(valor);
}

export default RegistrosDataset;
